import { ApiService } from "@/services/api-service";
import useAuthStore from "@/store/useAuthStore";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { ReactNode } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
} from "recharts";

export const Route = createFileRoute("/admin/_layout/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useAuthStore();

  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
    error: metricsError,
  } = useQuery({
    queryKey: ["metrics"],
    queryFn: ApiService.getDashboardCounts,
  });

  const {
    data: chartData,
    isLoading: isLoadingCharts,
    error: chartsError,
  } = useQuery({
    queryKey: ["chartData"],
    queryFn: ApiService.getDashboardChartData,
  });

  const { logout } = useAuthStore();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between flex-wrap min-h-14 py-4 sm:py-0">
            <div className="flex-shrink-0 flex items-center">
              <span className="text-lg font-bold text-gray-900">
                Admin Dashboard
              </span>
            </div>

            <div className="flex items-center gap-x-2">
              <span className="text-xs text-gray-500 mr-2">Welcome,</span>
              <span className="text-xs font-medium text-gray-900">
                {user?.name || "Admin"}
              </span>
              <button
                onClick={() => {
                  logout();
                  navigate({ to: "/auth/login" });
                }}
                className="cursor-pointer"
              >
                <LogOut className="w-4 h-4 text-red-500 " />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Section */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-700 mb-3">Metrics</h2>

          {isLoadingMetrics ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-white overflow-hidden shadow rounded-lg h-24 animate-pulse"
                >
                  <div className="px-4 py-4 sm:p-5">
                    <div className="h-2 bg-gray-200 rounded w-1/2 mb-3"></div>
                    <div className="h-4 bg-gray-300 rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : metricsError ? (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-xs">
              Error loading metrics. Please try again.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <MetricCard
                title="Total Users"
                value={metricsData?.users_count || 0}
              />
              <MetricCard
                title="Premium Users"
                value={metricsData?.premium_users_count || 0}
              />
              <MetricCard
                title="Freemium Users"
                value={metricsData?.freemium_users_count || 0}
              />
              <MetricCard
                title="Banned Users"
                value={metricsData?.banned_users_count || 0}
              />

              <MetricCard
                title="Total Projects"
                value={metricsData?.projects_count || 0}
              />
              <MetricCard
                title="Shared Projects"
                value={metricsData?.shared_projects_count || 0}
              />
              <MetricCard
                title="Personalizations"
                value={metricsData?.personalization_count || 0}
              />
              <MetricCard
                title="New Signups Today"
                value={metricsData?.new_signups_today_count || 0}
              />

              <MetricCard
                title="Chat Token Usage"
                value={metricsData?.usage?.chat_token_usage || 0}
              />
              <MetricCard
                title="Drafted Words"
                value={metricsData?.usage?.drafted_word_count || 0}
              />
              <MetricCard
                title="Context Attachments"
                value={metricsData?.usage?.project_context_count || 0}
              />
              <MetricCard
                title="Transcription (min)"
                value={Math.round(
                  (metricsData?.usage?.transcription_duration_sec || 0) / 60
                )}
              />
            </div>
          )}
        </div>

        {/* Charts Section */}
        <div className="mb-4">
          <h2 className="text-sm font-medium text-gray-700 mb-3">
            Usage Analytics
          </h2>

          {isLoadingCharts ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-white overflow-hidden shadow rounded-lg h-64 animate-pulse"
                >
                  <div className="px-4 py-4">
                    <div className="h-2 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="h-48 bg-gray-100 rounded-md"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : chartsError ? (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-xs">
              Error loading chart data. Please try again.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ChartCard title="Chat Token Usage">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={chartData?.daily_usage || []}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar
                      dataKey="chat_token_usage"
                      name="Chat Token Usage"
                      fill="#8884d8"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Drafted Word Count">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={chartData?.daily_usage || []}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar
                      dataKey="drafted_word_count"
                      name="Drafted Word Count"
                      fill="#82ca9d"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Project Context Count">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={chartData?.daily_usage || []}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar
                      dataKey="project_context_count"
                      name="Project Context Count"
                      fill="#ffc658"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Transcription Duration (sec)">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={chartData?.daily_usage || []}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar
                      dataKey="transcription_duration_sec"
                      name="Transcription Duration (sec)"
                      fill="#ff8042"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Personalization Stats">
                <ResponsiveContainer width="100%" height={250}>
                  <ComposedChart
                    data={chartData?.personalization || []}
                    margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="total_personalization_duration_sec"
                      stroke="#8884d8"
                      name="Duration (sec)"
                      strokeWidth={2}
                    />
                    <Bar
                      dataKey="document_count"
                      name="Document Count"
                      fill="#82ca9d"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="initial_personalizations"
                      name="Initial Personalizations"
                      fill="#ffc658"
                      radius={[4, 4, 0, 0]}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Documents Size">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={chartData?.personalization || []}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar
                      dataKey="documents_cumulative_size"
                      name="Size (bytes)"
                      fill="#8884d8"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: number }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-3 py-3 sm:p-4">
        <dt className="text-xs font-medium text-gray-500 truncate">{title}</dt>
        <dd className="mt-1 text-base font-semibold text-gray-900">{value}</dd>
      </div>
    </div>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-3 py-3 sm:p-4">
        <h3 className="text-xs font-medium text-gray-700 mb-2">{title}</h3>
        {children}
      </div>
    </div>
  );
}
